"use client";

import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ShoppingBag,
  Calendar,
  Clock,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type PreOrderOutput,
  type PreOrderQueryParams,
  PreOrderStatus,
} from "@/interfaces/pre-orders";
import { PreOrderService } from "@/services/PreOrderService";
import { RejectOrderDialog } from "./RejectOrderDialog";
import { useNavigate } from "react-router-dom";
import { numberToMoney } from "@/util/moneyConversion";
import { orderPaymentDetailsMethodTranslations } from "@/interfaces/payments";

interface PreOrderListProps {
  searchTerm?: string;
  refreshTrigger?: number;
  onRefresh: () => void;
  statusFilter: string;
  onStatusChange: (status: string) => void;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

export function PreOrderList({
  searchTerm = "",
  refreshTrigger = 0,
  onRefresh,
  statusFilter,
  onStatusChange,
}: PreOrderListProps) {
  const navigate = useNavigate();
  const [preOrders, setPreOrders] = useState<PreOrderOutput[]>([]);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [selectedPreOrderId, setSelectedPreOrderId] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    pages: 0,
  });

  const preOrderService = new PreOrderService();

  useEffect(() => {
    fetchPreOrders();
  }, [
    statusFilter,
    searchTerm,
    pagination.page,
    pagination.pageSize,
    refreshTrigger,
  ]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [statusFilter, searchTerm]);

  const fetchPreOrders = async () => {
    setIsLoading(true);
    try {
      const queryParams: PreOrderQueryParams = {
        expand: "offers",
        page: pagination.page,
        pageSize: pagination.pageSize,
      };

      if (statusFilter !== "all") {
        queryParams.status =
          PreOrderStatus[statusFilter as keyof typeof PreOrderStatus];
      }

      if (searchTerm) {
        queryParams.code = searchTerm;
      }

      const response = await preOrderService.findAll(queryParams);

      setPreOrders(
        response.data?.map((preOrder) => ({
          ...preOrder,
          status: preOrder.status || PreOrderStatus.PENDING,
        })) || []
      );

      setPagination({
        page: response.pagination.page || 1,
        pageSize: response.pagination.pageSize || 10,
        total: response.pagination.total || 0,
        pages: response.pagination.pages || 0,
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Erro ao buscar pré-pedidos:", error);
      setPreOrders([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        pages: 0,
      }));
      setIsLoading(false);
    }
  };

  const handleProcessPreOrder = (preOrderId: string) => {
    navigate(`/pre-pedidos/${preOrderId}`);
  };

  const confirmReject = async () => {
    if (!selectedPreOrderId) return;

    try {
      setIsLoading(true);
      await preOrderService.updateStatus(
        selectedPreOrderId,
        PreOrderStatus.REJECTED,
        {
          expand: "offers",
        }
      );

      fetchPreOrders();
      setIsRejectDialogOpen(false);
      setSelectedPreOrderId(null);
    } catch (error) {
      console.error("Erro ao rejeitar pedido:", error);
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  };

  // Função para obter o valor total do pedido da API
  const getOrderTotal = (preOrder: PreOrderOutput): number => {
    // Usar o totalAmount retornado pela API, com fallback para 0
    return preOrder.totalAmount || 0;
  };

  // Função para renderizar o badge de status com cores apropriadas
  const renderStatusBadge = (status: PreOrderStatus) => {
    switch (status) {
      case PreOrderStatus.PENDING:
        return (
          <Badge
            variant="outline"
            className="text-amber-600 bg-transparent border-amber-200"
          >
            Pendente
          </Badge>
        );
      case PreOrderStatus.ACCEPTED:
        return (
          <Badge
            variant="outline"
            className="text-green-600 bg-transparent border-green-200"
          >
            Aceito
          </Badge>
        );
      case PreOrderStatus.REJECTED:
        return (
          <Badge
            variant="outline"
            className="text-red-600 bg-transparent border-red-200"
          >
            Recusado
          </Badge>
        );
      default:
        return (
          <Badge
            variant="outline"
            className="text-gray-600 bg-transparent border-gray-200"
          >
            {status}
          </Badge>
        );
    }
  };

  let content: ReactNode;

  if (isLoading) {
    content = (
      <div className="text-center py-12">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-700"></div>
        </div>
        <p className="mt-4 text-gray-500">Carregando pré-pedidos...</p>
      </div>
    );
  } else if (!preOrders || preOrders.length === 0) {
    content = (
      <div className="text-center py-12">
        <p className="text-gray-500">
          {searchTerm || statusFilter !== "all"
            ? "Não há pré-pedidos correspondentes aos filtros aplicados."
            : "Não há pré-pedidos disponíveis."}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          className="mt-4 text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Atualizar
        </Button>
      </div>
    );
  } else {
    content = (
      <div>
        <div className="hidden md:block">
          <div className="overflow-x-auto px-4 py-4">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow className="border-b border-gray-100">
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Código
                  </TableHead>
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Data
                  </TableHead>
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Criado há
                  </TableHead>
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Cliente
                  </TableHead>
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Pagamento
                  </TableHead>
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Entrega
                  </TableHead>
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Total
                  </TableHead>
                  <TableHead className="text-xs text-gray-500 font-medium">
                    Status
                  </TableHead>
                  <TableHead className="text-right text-xs text-gray-500 font-medium">
                    Ações
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preOrders.map((preOrder) => {
                  const totalValue = getOrderTotal(preOrder);

                  return (
                    <TableRow
                      key={preOrder.id}
                      className="hover:bg-gray-50 border-b border-gray-100"
                    >
                      <TableCell className="font-medium text-sm">
                        {preOrder.code}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" />
                          {format(
                            new Date(preOrder.createdAt),
                            "dd/MM/yyyy HH:mm",
                            { locale: ptBR }
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          {formatDistanceToNow(new Date(preOrder.createdAt), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {preOrder.customer.name}
                      </TableCell>
                      <TableCell className="text-sm">
                        {
                          orderPaymentDetailsMethodTranslations[
                            preOrder.paymentMethod
                          ]
                        }
                      </TableCell>
                      <TableCell className="text-sm">
                        {preOrder.delivery.deliveryType === "WITHDRAWAL"
                          ? "Retirada"
                          : "Entrega"}
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {numberToMoney(totalValue)}
                      </TableCell>
                      <TableCell>
                        {renderStatusBadge(
                          (preOrder.status as PreOrderStatus) ??
                            PreOrderStatus.PENDING
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleProcessPreOrder(preOrder.id)}
                          className="h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-gray-200"
                        >
                          <ArrowRight className="h-4 w-4 mr-1" />
                          Ver Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="space-y-3 px-4 py-4 md:hidden">
          {preOrders.map((preOrder) => {
            const totalValue = getOrderTotal(preOrder);

            return (
              <div
                key={preOrder.id}
                className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {preOrder.code}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                      <Clock className="h-3.5 w-3.5 text-gray-400" />
                      {formatDistanceToNow(new Date(preOrder.createdAt), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                  <div className="shrink-0">
                    {renderStatusBadge(
                      (preOrder.status as PreOrderStatus) ??
                        PreOrderStatus.PENDING
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {format(new Date(preOrder.createdAt), "dd/MM/yyyy HH:mm", {
                    locale: ptBR,
                  })}
                </div>
                <div className="mt-3 space-y-3 text-sm text-gray-700">
                  <div>
                    <span className="text-xs font-medium uppercase text-gray-500">
                      Cliente
                    </span>
                    <p className="font-semibold text-gray-900">
                      {preOrder.customer.name}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-xs font-medium uppercase text-gray-500">
                        Pagamento
                      </span>
                      <p className="font-semibold text-gray-900">
                        {
                          orderPaymentDetailsMethodTranslations[
                            preOrder.paymentMethod
                          ]
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-medium uppercase text-gray-500">
                        Entrega
                      </span>
                      <p className="font-semibold text-gray-900">
                        {preOrder.delivery.deliveryType === "WITHDRAWAL"
                          ? "Retirada"
                          : "Entrega"}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs font-medium uppercase text-gray-500">
                        Total
                      </span>
                      <p className="text-base font-semibold text-[#EE7B11]">
                        {numberToMoney(totalValue)}
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleProcessPreOrder(preOrder.id)}
                  className="mt-4 w-full justify-center border-gray-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                >
                  <ArrowRight className="h-4 w-4 mr-1" />
                  Ver Detalhes
                </Button>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-100">
          <div className="text-sm text-gray-500">
            Mostrando{" "}
            {pagination.total > 0
              ? (pagination.page - 1) * pagination.pageSize + 1
              : 0}{" "}
            a{" "}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)}{" "}
            de {pagination.total} pedidos
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center mr-4">
              <span className="text-sm text-gray-500 mr-2">
                Itens por página:
              </span>
              <Select
                value={pagination.pageSize.toString()}
                onValueChange={(value) => handlePageSizeChange(Number(value))}
              >
                <SelectTrigger className="h-8 w-[70px] border-gray-200">
                  <SelectValue placeholder={pagination.pageSize.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-gray-200"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                let pageNumber: number;

                if (pagination.pages <= 5) {
                  pageNumber = i + 1;
                } else if (pagination.page <= 3) {
                  pageNumber = i + 1;
                } else if (pagination.page >= pagination.pages - 2) {
                  pageNumber = pagination.pages - 4 + i;
                } else {
                  pageNumber = pagination.page - 2 + i;
                }

                return (
                  <Button
                    key={pageNumber}
                    variant={
                      pagination.page === pageNumber ? "default" : "outline"
                    }
                    size="icon"
                    className={`h-8 w-8 border-gray-200 ${
                      pagination.page === pageNumber
                        ? "bg-gray-900 hover:bg-gray-800"
                        : ""
                    }`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-gray-200"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card className="border-0 shadow-sm">
        <CardHeader className="border-b pb-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-gray-500" />
              <CardTitle className="text-lg font-medium">Pré-Pedidos</CardTitle>
            </div>
            <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center md:justify-end md:gap-4">
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  className="h-8 w-full text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 sm:w-auto"
                >
                  <div className="flex items-center justify-center">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    <span className="sm:inline">Atualizar</span>
                  </div>
                </Button>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
                  <span className="text-sm text-gray-500">Status:</span>
                  <Select value={statusFilter} onValueChange={onStatusChange}>
                    <SelectTrigger className="w-full h-9 text-sm border-gray-200 sm:w-[180px]">
                      <SelectValue placeholder="Pendentes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value={PreOrderStatus.PENDING}>
                        Pendentes
                      </SelectItem>
                      <SelectItem value={PreOrderStatus.ACCEPTED}>
                        Aceitos
                      </SelectItem>
                      <SelectItem value={PreOrderStatus.REJECTED}>
                        Recusados
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Badge
                variant="outline"
                className="bg-transparent text-gray-600 border-gray-200 self-start md:self-auto"
              >
                {pagination.total} pedido(s)
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {content}
        </CardContent>
      </Card>

      <RejectOrderDialog
        open={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onReject={confirmReject}
      />
    </div>
  );
}
